import type { ShopifyProduct } from "@/lib/shopify";

/**
 * Bundle discounts are RULES, not SKUs.
 *
 * A bundle is never a product: the customer always buys the individual SKUs,
 * and the saving is applied as a cart-level discount. That keeps GA4 / Meta
 * `items` payloads per-product and the Merchant feed unchanged.
 *
 * Rules live in `public.bundle_rules` and are managed from the admin panel.
 * `DEFAULT_BUNDLE_RULES` mirrors the seeded rows and is used as a fallback
 * whenever the table can't be read, so pricing never breaks.
 */

/** Rule shape shared by client, server functions and pricing helpers. */
export interface BundleRule {
  slug: string;
  title: string;
  tagline: string;
  goal: string;
  /** Product handles that must all be present in the cart. */
  matchHandles: string[];
  /** Bottles per SKU. */
  quantityPerItem: number;
  /** Discount in basis points (1500 = 15%). Ignored when fixedPricePaise set. */
  discountBps: number;
  /** Fixed bundle price in paise (overrides discountBps). */
  fixedPricePaise: number | null;
  featured: boolean;
  sortOrder: number;
}

/**
 * Legacy rates, kept as the fallback for the seeded duo/trio rules and for
 * quiz/PDP previews that price a selection before rules have loaded.
 */
export const DUO_BUNDLE_RATE = 0.1;
export const TRIO_BUNDLE_RATE = 0.15;

/** Rate for a bundle of `n` products. */
export function bundleRateForCount(n: number): number {
  if (n >= 3) return TRIO_BUNDLE_RATE;
  if (n === 2) return DUO_BUNDLE_RATE;
  return 0;
}

/** Bundle math off a list of selected pack prices (rupees). */
export function computeBundlePricing(prices: number[]): {
  subtotal: number;
  rate: number;
  total: number;
  savings: number;
} {
  const subtotal = prices.reduce((s, p) => s + p, 0);
  const rate = bundleRateForCount(prices.length);
  const total = Math.round(subtotal * (1 - rate));
  return { subtotal: Math.round(subtotal), rate, total, savings: Math.round(subtotal) - total };
}

export const DEFAULT_BUNDLE_RULES: BundleRule[] = [
  {
    slug: "calm-sleep",
    title: "Calm & Sleep Duo",
    tagline: "Magnesium for deep rest + Liver Biome Plus for overnight support.",
    goal: "Better sleep & recovery",
    matchHandles: ["mag-absorb-pro", "liver-biome-plus"],
    quantityPerItem: 1,
    discountBps: 1000,
    fixedPricePaise: null,
    featured: false,
    sortOrder: 1,
  },
  {
    slug: "balance-calm-duo",
    title: "Balance & Calm Duo",
    tagline:
      "Ova Balance for cycle and metabolic support, paired with Mag Absorb Pro for magnesium, sleep and recovery support.",
    goal: "Cycle & magnesium support",
    matchHandles: ["ova-balance", "mag-absorb-pro"],
    quantityPerItem: 1,
    discountBps: 1000,
    fixedPricePaise: null,
    featured: false,
    sortOrder: 2,
  },
  {
    slug: "balance-detox-duo",
    title: "Balance & Detox Duo",
    tagline:
      "Ova Balance for cycle and metabolic support, paired with Liver Biome Plus for liver and gut wellness.",
    goal: "Cycle, liver & gut support",
    matchHandles: ["ova-balance", "liver-biome-plus"],
    quantityPerItem: 1,
    discountBps: 1000,
    fixedPricePaise: null,
    featured: false,
    sortOrder: 3,
  },
  {
    slug: "full-ova-balance-protocol",
    title: "Full Ova Balance Protocol",
    tagline: "Ova Balance, Mag Absorb Pro and Liver Biome Plus in one structured daily protocol.",
    goal: "Complete daily protocol",
    matchHandles: ["ova-balance", "mag-absorb-pro", "liver-biome-plus"],
    quantityPerItem: 1,
    discountBps: 1500,
    fixedPricePaise: null,
    featured: true,
    sortOrder: 4,
  },
];


/** Row shape as returned by the database / server functions. */
export interface BundleRuleRow {
  slug: string;
  title: string;
  tagline: string | null;
  goal: string | null;
  match_handles: string[] | null;
  quantity_per_item: number | null;
  discount_bps: number | null;
  fixed_price_paise: number | null;
  featured: boolean | null;
  sort_order: number | null;
}

export function ruleFromRow(row: BundleRuleRow): BundleRule {
  return {
    slug: row.slug,
    title: row.title,
    tagline: row.tagline ?? "",
    goal: row.goal ?? "",
    matchHandles: row.match_handles ?? [],
    quantityPerItem: row.quantity_per_item ?? 1,
    discountBps: row.discount_bps ?? 0,
    fixedPricePaise: row.fixed_price_paise ?? null,
    featured: Boolean(row.featured),
    sortOrder: row.sort_order ?? 0,
  };
}

/**
 * Client-side registry: the browser fetches live rules once and registers
 * them here so pure pricing helpers (cart, PDP) pick them up without every
 * call site threading a prop.
 */
/**
 * `null` = configuration not loaded yet (fall back to seeded defaults).
 * `[]`   = loaded and authoritatively empty (no bundle discounts at all).
 */
let activeRules: BundleRule[] | null = null;

export function setActiveBundleRules(rules: BundleRule[] | null | undefined): void {
  activeRules = rules == null ? null : [...rules].sort((a, b) => a.sortOrder - b.sortOrder);
}

export function getActiveBundleRules(): BundleRule[] {
  return activeRules ?? DEFAULT_BUNDLE_RULES;
}

/**
 * Normalise whatever we got into a usable rule list. An explicitly supplied
 * empty array is honoured as "no bundles"; `undefined`/`null` means "not
 * supplied", so the registry (or seeded defaults) decides.
 */
export function resolveRules(rules?: BundleRule[] | null): BundleRule[] {
  if (rules == null) return getActiveBundleRules();
  return [...rules].sort((a, b) => a.sortOrder - b.sortOrder);
}

/** Display rate (0–1) for a rule, when it isn't fixed-price. */
export function ruleRate(rule: BundleRule): number {
  return Math.max(0, Math.min(0.9, rule.discountBps / 10000));
}

/** Products of a rule, in rule order. Missing SKUs are skipped. */
export function resolveBundleProducts(
  rule: BundleRule,
  products: ShopifyProduct["node"][],
): ShopifyProduct["node"][] {
  const out: ShopifyProduct["node"][] = [];
  for (const handle of rule.matchHandles) {
    const found = products.find((p) => p.handle === handle);
    if (found) out.push(found);
  }
  return out;
}
